import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LockClosedIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { createLoyaltyClass } from '../../generate/services/googleWalletService';
import GoogleWalletPassPreview from './GoogleWalletPassPreview';
import { useAuth } from '../../auth/hooks/useAuth';
import { useMerchantAuth } from '../../../contexts/AuthContext';
import { useAppSelector } from '../../../shared/store/hooks';
import { useDispatch } from 'react-redux';
import { settingsSliceAPI } from '../store/settingsSlice';
import { rewardsSliceAPI } from '../../rewards/store/rewardsSlice';
import { normalizeUrl } from '../../../shared/utils/urlUtils';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
import { VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED } from '../../../shared/config/featureFlags';
import { getUsage } from '../services/tierApi';
import {
    DEFAULT_PASS_BACKGROUND_COLOR,
    DEFAULT_PASS_TEXT_COLOR,
} from '../../../shared/constants/loyalPassBrand';

function parseOptionalPassImageUrlInput(raw) {
    const t = (raw || '').trim();
    if (!t) {
        return { ok: true, value: null };
    }
    const u = normalizeUrl(t);
    try {
        const parsed = new URL(u);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            return { ok: false, error: 'URL must use http or https.' };
        }
        return { ok: true, value: parsed.href };
    } catch {
        return { ok: false, error: 'Enter a valid URL.' };
    }
}

function validateBackgroundImageOnly(backgroundRaw) {
    const errors = {};
    const background = parseOptionalPassImageUrlInput(backgroundRaw);
    if (!background.ok) {
        errors.background = background.error;
    }
    if (Object.keys(errors).length > 0) {
        return { ok: false, errors, values: null };
    }
    return {
        ok: true,
        errors: {},
        values: { pass_background_image_url: background.value },
    };
}

function validateStripAndBackgroundImages(stripRaw, backgroundRaw) {
    const errors = {};
    const strip = parseOptionalPassImageUrlInput(stripRaw);
    const background = parseOptionalPassImageUrlInput(backgroundRaw);
    if (!strip.ok) {
        errors.strip = strip.error;
    }
    if (!background.ok) {
        errors.background = background.error;
    }
    if (Object.keys(errors).length > 0) {
        return { ok: false, errors, values: null };
    }
    return {
        ok: true,
        errors: {},
        values: {
            pass_strip_image_url: strip.value,
            pass_background_image_url: background.value,
        },
    };
}

export default function AndroidPassSettings() {
    const [programName, setProgramName] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [brandColor, setBrandColor] = useState(DEFAULT_PASS_BACKGROUND_COLOR);
    const [textColor, setTextColor] = useState(DEFAULT_PASS_TEXT_COLOR);
    const [stripImageUrl, setStripImageUrl] = useState('');
    const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
    const [passImageErrors, setPassImageErrors] = useState({});
    const [passImageSaveMessage, setPassImageSaveMessage] = useState(null);
    const [passImageSaveType, setPassImageSaveType] = useState('');
    const [passImageSaving, setPassImageSaving] = useState(false);
    const [templateSuccess, setTemplateSuccess] = useState(null);
    const [error, setError] = useState('');
    const [classExists, setClassExists] = useState(false);
    const [loading, setLoading] = useState(true);
    const [merchantTier, setMerchantTier] = useState(null);

    const dispatch = useDispatch();
    const {
        state: { userId },
    } = useAuth();
    const merchantAuth = useMerchantAuth();
    const effectiveMerchantId = merchantAuth?.user?.merchantId ?? merchantAuth?.user?.id ?? userId;
    const generalSettingsFetchStatus = useAppSelector((state) => state.settings.generalSettingsFetchStatus);
    const settingsLoading = useAppSelector((state) => state.settings.loading);
    const generalsettings = useAppSelector((state) => state.settings.generalsettings);
    const rewardsettings = useAppSelector((state) => state.settings.rewardsettings);
    const rewardsObj = useAppSelector((state) => state.rewards.rewards);
    const rewardsLoaded = useAppSelector((state) => state.rewards.hasLoaded);
    const generalSettingsReady =
        generalSettingsFetchStatus === 'succeeded' || generalSettingsFetchStatus === 'failed';
    const formBlocking = !generalSettingsReady || settingsLoading;
    const { name: merchantName } = generalsettings || {};

    const visitRewardsProgramReady = useMemo(() => {
        if (!VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED || !rewardsettings?.visits_enabled) {
            return false;
        }
        const list = Object.values(rewardsObj || {});
        return list.some((r) => r && r.type === 'visits' && r.enabled !== false);
    }, [rewardsettings?.visits_enabled, rewardsObj]);

    useEffect(() => {
        if (!generalsettings) {
            return;
        }
        setProgramName(generalsettings.program_name || '');
        setLogoUrl(generalsettings.logo_url || '');
        setBrandColor(generalsettings.pass_background_color || DEFAULT_PASS_BACKGROUND_COLOR);
        setTextColor(generalsettings.pass_text_color || DEFAULT_PASS_TEXT_COLOR);
        setStripImageUrl((generalsettings.pass_strip_image_url || '').trim());
        setBackgroundImageUrl((generalsettings.pass_background_image_url || '').trim());
    }, [generalsettings]);

    useEffect(() => {
        if (generalSettingsFetchStatus === 'idle') {
            dispatch(settingsSliceAPI.fetchGeneralSettings());
        }
    }, [dispatch, generalSettingsFetchStatus]);

    useEffect(() => {
        if (rewardsettings === undefined || rewardsettings?.point_per_dollar === undefined) {
            dispatch(settingsSliceAPI.fetchRewardSettings());
        }
    }, [dispatch, rewardsettings]);

    useEffect(() => {
        if (!rewardsLoaded) {
            dispatch(rewardsSliceAPI.fetchRewards());
        }
    }, [dispatch, rewardsLoaded]);

    useEffect(() => {
        let cancelled = false;
        getUsage()
            .then((res) => {
                if (!cancelled) {
                    const t = (res?.data?.tier ?? '').toString().toLowerCase();
                    setMerchantTier(t || null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setMerchantTier(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (formBlocking) {
            setLoading(true);
            return;
        }
        setLoading(false);
        setClassExists(!!generalsettings?.google_class_id);
    }, [formBlocking, generalsettings?.google_class_id]);

    const validatePassImagesForSave = () =>
        visitRewardsProgramReady
            ? validateStripAndBackgroundImages(stripImageUrl, backgroundImageUrl)
            : validateBackgroundImageOnly(backgroundImageUrl);

    const handleSavePassImages = async (e) => {
        e?.preventDefault?.();
        if (isFreePlan || !classExists) {
            return;
        }
        if (!effectiveMerchantId) {
            setPassImageSaveMessage('Merchant not found. Please log in again.');
            setPassImageSaveType('error');
            return;
        }

        const validated = validatePassImagesForSave();
        if (!validated.ok) {
            setPassImageErrors(validated.errors);
            setPassImageSaveMessage(null);
            setPassImageSaveType('');
            return;
        }
        setPassImageErrors({});

        setPassImageSaving(true);
        setPassImageSaveMessage(null);
        setPassImageSaveType('');
        try {
            await dispatch(
                settingsSliceAPI.updateGeneralSettings({
                    ...generalsettings,
                    ...validated.values,
                })
            ).unwrap();
            setPassImageSaveMessage('Pass images saved. New installs and refreshes can pick them up where supported.');
            setPassImageSaveType('success');
        } catch (err) {
            setPassImageSaveMessage(extractErrorMessage(err) || 'Could not save pass images.');
            setPassImageSaveType('error');
        } finally {
            setPassImageSaving(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!effectiveMerchantId) {
            setError('Merchant not found. Please log in again.');
            return;
        }

        if (classExists) {
            return;
        }

        const validated = validatePassImagesForSave();
        if (!validated.ok) {
            setPassImageErrors(validated.errors);
            setError('Fix optional pass image URLs below, or clear invalid fields.');
            return;
        }
        setPassImageErrors({});

        setTemplateSuccess(null);
        setError('');
        try {
            const { data } = await createLoyaltyClass(programName, logoUrl, brandColor, textColor);
            await dispatch(
                settingsSliceAPI.updateGeneralSettings({
                    ...generalsettings,
                    program_name: programName,
                    pass_background_color: brandColor,
                    pass_text_color: textColor,
                    ...validated.values,
                    // Avoid PATCH echoing stale `google_class_id: null` from Redux; link new id immediately.
                    ...(data?.classId ? { google_class_id: data.classId } : {}),
                })
            ).unwrap();
            await dispatch(settingsSliceAPI.fetchGeneralSettings()).unwrap();
            setTemplateSuccess(
                data?.alreadyExists
                    ? {
                          existingClass: true,
                          message:
                              data?.message ||
                              'That Google Wallet class already existed; it has been linked to your account.',
                      }
                    : { existingClass: false, message: 'Pass template created successfully.' }
            );
        } catch (err) {
            setTemplateSuccess(null);
            const msg =
                err?.response?.data?.message ||
                extractErrorMessage(err) ||
                err?.message ||
                'Failed to create Google Wallet template';
            setError(msg);
        }
    };

    const previewProgramName = classExists
        ? generalsettings?.program_name || programName
        : programName || generalsettings?.program_name || 'Program';

    const stripDisabled = loading || !visitRewardsProgramReady;
    const isFreePlan = merchantTier === 'free';
    const backgroundFieldDisabled = loading || isFreePlan;
    const passImageSaveDisabled = loading || passImageSaving || isFreePlan || !classExists;

    useEffect(() => {
        if (merchantTier === 'free') {
            setBackgroundImageUrl('');
        }
    }, [merchantTier]);

    return (
        <div className="min-h-screen p-8">
            <div className="flex flex-col md:flex-row gap-8 max-w-5xl mx-auto">
                <div className="flex-1 bg-white p-8 rounded-3xl shadow-xl">
                    <h2 className="text-2xl font-bold text-primary mb-6">Android Program Settings</h2>
                    <p className="mb-4 flex items-start gap-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                        <InformationCircleIcon
                            className="h-5 w-5 shrink-0 mt-0.5 text-amber-800"
                            aria-hidden
                        />
                        <span>
                        <strong>Note:</strong> Template information (program name, logo, pass colors) can only be set
                        once.
                        <span className="block mt-2 text-amber-900/90">
                            <strong>Google Wallet:</strong> Your pass class uses a hex background color and logo from
                            this page. A valid business address in General Settings is required so we can set a store
                            location on the class. The preview is approximate—Android may render spacing and typography
                            differently.
                        </span>
                        </span>
                    </p>
                    {error && (
                        <div className="mb-4 p-3 rounded bg-red-100 text-red-700 font-semibold border border-red-300">
                            {error}
                        </div>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-4" data-tutorial="android-pass-form">
                        <div>
                            <label className="block mb-1 text-sm font-semibold">Program Name</label>
                            <input
                                type="text"
                                value={programName}
                                onChange={(e) => setProgramName(e.target.value)}
                                disabled={classExists || loading}
                                className={`w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-300 ${(classExists || loading) ? 'bg-gray-100 cursor-not-allowed opacity-80' : ''}`}
                                required
                            />
                        </div>

                        <p className="text-sm text-gray-600">
                            Logo is set in <strong>Settings → General</strong> (Branding → Logo link).
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block mb-1 text-sm font-semibold">Pass background color</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        value={brandColor}
                                        onChange={(e) => setBrandColor(e.target.value)}
                                        disabled={classExists || loading}
                                        className={`h-10 w-14 rounded border border-gray-300 ${(classExists || loading) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                    />
                                    <input
                                        type="text"
                                        value={brandColor}
                                        onChange={(e) => setBrandColor(e.target.value)}
                                        disabled={classExists || loading}
                                        className={`flex-1 p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-300 ${(classExists || loading) ? 'bg-gray-100 cursor-not-allowed opacity-80' : ''}`}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-semibold">Pass text / label color</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        value={textColor}
                                        onChange={(e) => setTextColor(e.target.value)}
                                        disabled={classExists || loading}
                                        className={`h-10 w-14 rounded border border-gray-300 ${(classExists || loading) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                    />
                                    <input
                                        type="text"
                                        value={textColor}
                                        onChange={(e) => setTextColor(e.target.value)}
                                        disabled={classExists || loading}
                                        className={`flex-1 p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-300 ${(classExists || loading) ? 'bg-gray-100 cursor-not-allowed opacity-80' : ''}`}
                                    />
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            onClick={handleSubmit}
                            className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded text-white ${
                                classExists ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-purple-400'
                            }`}
                            disabled={classExists || loading}
                        >
                            {loading ? (
                                'Checking...'
                            ) : classExists ? (
                                <>
                                    <LockClosedIcon className="h-5 w-5 shrink-0" aria-hidden />
                                    Template Already Created
                                </>
                            ) : (
                                'Create Pass Template'
                            )}
                        </button>

                        {templateSuccess && (
                            <p
                                className={
                                    templateSuccess.existingClass
                                        ? 'mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'
                                        : 'mt-3 text-green-600'
                                }
                                role="status"
                            >
                                {templateSuccess.existingClass ? (
                                    <span>{templateSuccess.message}</span>
                                ) : (
                                    <span>✅ {templateSuccess.message}</span>
                                )}
                            </p>
                        )}
                    </form>

                    <div className="mt-10 border-t border-gray-200 pt-8">
                        <h3 className="text-lg font-semibold text-gray-900">Google Wallet Pass Extras</h3>
                        {merchantTier === 'free' && (
                            <p className="mt-2 flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-950">
                                <LockClosedIcon
                                    className="h-4 w-4 shrink-0 mt-0.5 text-sky-800"
                                    aria-hidden
                                />
                                <span>
                                Extra customization not included in <strong>Free plan</strong>.{' '}
                                <Link to="/dashboard/settings/membership" className="font-semibold underline">
                                    See Membership
                                </Link>
                                .
                                </span>
                            </p>
                        )}
                        <p className="mt-1 text-xs text-gray-600">
                            Public <code className="rounded bg-gray-100 px-1">https</code> URLs to PNG or JPEG. Our
                            servers download them when a customer installs or refreshes a pass; we do not host those
                            image files. Recommended sizes: strip wide (~1125x369px), background portrait (~360x440px).
                        </p>
                        {!visitRewardsProgramReady && VISIT_AND_PUNCH_CARD_REWARDS_UI_ENABLED && (
                            <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                For the <strong>visit-based</strong> strip path today: turn on{' '}
                                <strong>Enable Visit Rewards</strong> in{' '}
                                <Link to="/dashboard/settings/global-rewards" className="font-semibold underline">
                                    Rewards Settings
                                </Link>
                                , then add a{' '}
                                <Link to="/dashboard/rewards" className="font-semibold underline">
                                    Visits
                                </Link>{' '}
                                reward before saving a strip URL (Wallet will ignore the strip until then). Spend- or
                                package-based punches will use different setup as those features roll out.
                            </p>
                        )}
                        <div className="mt-4 space-y-3">
                            <div>
                                <label htmlFor="androidPassStripUrl" className="mb-1 block text-sm font-medium text-gray-700">
                                    Strip image URL (punch-card style)
                                </label>
                                <input
                                    id="androidPassStripUrl"
                                    type="text"
                                    value={stripImageUrl}
                                    onChange={(e) => setStripImageUrl(e.target.value)}
                                    placeholder="https://cdn.example.com/wallet/strip.png"
                                    disabled={stripDisabled}
                                    className={`w-full rounded-xl border p-3 ${
                                        passImageErrors.strip ? 'border-red-300' : 'border-gray-300'
                                    } ${stripDisabled ? 'cursor-not-allowed bg-gray-50 opacity-80' : ''}`}
                                />
                                {passImageErrors.strip && (
                                    <p className="mt-1 text-sm text-red-500">{passImageErrors.strip}</p>
                                )}
                            </div>
                            <div>
                                <label
                                    htmlFor="androidPassBackgroundUrl"
                                    className="mb-1 block text-sm font-medium text-gray-700"
                                >
                                    Background image URL
                                </label>
                                {isFreePlan && (
                                    <p className="mb-1 text-xs text-gray-500">Not available on the Free plan.</p>
                                )}
                                <input
                                    id="androidPassBackgroundUrl"
                                    type="text"
                                    value={backgroundImageUrl}
                                    onChange={(e) => setBackgroundImageUrl(e.target.value)}
                                    placeholder="https://cdn.example.com/wallet/bg.png"
                                    disabled={backgroundFieldDisabled}
                                    className={`w-full rounded-xl border p-3 ${
                                        passImageErrors.background ? 'border-red-300' : 'border-gray-300'
                                    } ${backgroundFieldDisabled ? 'cursor-not-allowed bg-gray-50 opacity-80' : ''}`}
                                />
                                {passImageErrors.background && (
                                    <p className="mt-1 text-sm text-red-500">{passImageErrors.background}</p>
                                )}
                            </div>
                        </div>
                        {passImageSaveMessage && (
                            <p
                                className={`mt-3 text-sm font-medium ${
                                    passImageSaveType === 'success' ? 'text-green-700' : 'text-red-700'
                                }`}
                                role="status"
                            >
                                {passImageSaveMessage}
                            </p>
                        )}
                        <button
                            type="button"
                            onClick={handleSavePassImages}
                            disabled={passImageSaveDisabled}
                            className="mt-4 inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition hover:bg-purple-400 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {passImageSaving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>

                <div className="md:w-[320px] shrink-0">
                    <p className="mb-2 text-xs text-gray-500">
                        Preview updates as you edit colors, program name, and image URLs. The strip preview reflects the
                        visit-based punch path when Enable Visit Rewards is on and a Visits reward exists; other punch
                        models (spend, packages) may look different on device as they ship.
                    </p>
                    <GoogleWalletPassPreview
                        merchantName={merchantName || 'Your Business Name'}
                        programName={previewProgramName}
                        logoUrl={logoUrl || generalsettings?.logo_url || ''}
                        stripImageUrl={visitRewardsProgramReady ? stripImageUrl : ''}
                        backgroundImageUrl={isFreePlan ? '' : backgroundImageUrl}
                        passBackgroundColor={brandColor}
                        passTextColor={textColor}
                        points={0}
                        visits={0}
                    />
                </div>
            </div>
        </div>
    );
}
